import React, {
    createContext,
    useRef,
    useContext,
    useEffect,
    useState,
} from "react";
import { buildFeaturedImageUrl } from "../utils/general";
// Create the PlayerContext Context
export const PlayerContext = createContext();

// Custom hook to use the Player Context
export const usePlayer = () => useContext(PlayerContext);

const getPlayerActiveStatus = async (streamId, setActiveStatus) => {
    try {
        const response = await fetch(
            `https://churchcamlive.ie/data/player-status/${streamId}.json?t=${Date.now()}`,
            {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            }
        );

        if (!response.ok) {
            throw new Error("Failed to fetch Recording data");
        }

        const data = await response.json();
        const activeStatus = data["a"] === "1" ? true : false;
        setActiveStatus(activeStatus);
        console.log("Player active status:", data["a"]);
    } catch (error) {
        console.error("Error fetching player active status:", error);
    }
};

const getPlayerNavActiveStatus = async (streamId, setHasNavigation) => {
    try {
        const response = await fetch(
            `https://churchcamlive.ie/data/nav/${streamId}.json?t=${Date.now()}`,
            {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            }
        );

        if (!response.ok) {
            throw new Error("Failed to fetch Recording data");
        }

        const data = await response.json();
        const navStatus = data["a"] === "1" ? true : false;
        setHasNavigation(navStatus);
        console.log("Player nav active status:", data["a"]);
    } catch (error) {
        console.error(
            "Error fetching player nav active status (file may not exist yet) :",
            error
        );
    }
};

const getStreamSingleMeta = async (
    streamId,
    slug,
    setActiveStatus,
    setBackgroundImage,
    setPasswordProtected,
    setStreamId,
    passwordVerified
) => {
    try {
        const response = await fetch(
            `https://churchcamlive.ie/data/single/${slug}.json?t=${Date.now()}`,
            {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            }
        );
        if (!response.ok) {
            throw new Error("Failed to fetch stream meta data");
        }
        const data = await response.json();
        if (!passwordVerified) { setPasswordProtected(data.content.protected) };
        console.log("Password protected:", data.content.protected);


        setBackgroundImage(buildFeaturedImageUrl(slug, data.featured_media));

        console.log("Stream meta data:", data);
    } catch (error) {
        console.error("Error fetching stream meta data:", error);
    }
};

const getFilteredStream = async (slug, setStreamId) => {
    try {
        const response = await fetch(
            `https://churchcamlive.ie/data/player-filter/${slug}.json?t=${Date.now()}`,
            {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            }
        );
        if (!response.ok) {
            throw new Error("Failed to fetch stream data");
        }
        const data = await response.json();
        const filteredStream = data.filter_id;
        if (data.filter_status === true) {
            setStreamId(filteredStream);
        }
    } catch (error) {
        console.log("Error fetching stream data:", error);
    }
};

const runContextSetters = async (
    streamId,
    slug,
    setActiveStatus,
    setHasNavigation,
    setBackgroundImage,
    setPasswordProtected,
    setStreamId,
    passwordVerified
) => {
    console.log("Running context setters...");
    await getPlayerActiveStatus(streamId, setActiveStatus);
    await getPlayerNavActiveStatus(streamId, setHasNavigation);

    await getStreamSingleMeta(
        streamId,
        slug,
        setActiveStatus,
        setBackgroundImage,
        setPasswordProtected,
        setStreamId,
        passwordVerified
    );

    await getFilteredStream(streamId, setStreamId);
};

const authenticateUser = async (
    streamId,
    password,
    setPasswordProtected,
    setPasswordProtectedError,
    setPasswordVerified
) => {
    try {
        console.log("Authenticating user...");
        console.log("Stream ID:", streamId);
        console.log("Password:", password);
        const response = await fetch(
            `https://churchcamlive.ie/wp-json/ccl_admin/v2/streams/${streamId}/access`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    password: password,
                }),
            }
        );
        if (!response.ok) {
            throw new Error("Failed to authenticate user");
        }
        const data = await response.json();

        setPasswordProtected(false);
        setPasswordVerified(true);
        setPasswordProtectedError("");

        return data;
    } catch (error) {
        console.log("Error authenticating user:", error);
        setPasswordProtectedError(error.message);
        setPasswordProtected(true);
        setPasswordVerified(false);
        return null;
    }
};

export const PlayerProvider = ({ children }) => {
    const [streamId, setStreamId] = useState("");
    const [slug, setSlug] = useState("");
    const [activeStatus, setActiveStatus] = useState(false);
    const [activeTab, setActiveTab] = useState("live"); // Default to "live"
    const [backgroundImage, setBackgroundImage] = useState(""); // Default to empty string
    const [passwordProtected, setPasswordProtected] = useState(false);
    const [passwordVerified, setPasswordVerified] = useState(false);
    const [passwordProtectedError, setPasswordProtectedError] = useState("");
    const [hasNavigation, setHasNavigation] = useState(true);
    const [navExpanded, setNavExpanded] = useState(false);
    const [navVisible, setNavVisible] = useState(true);
    const hoverTimeout = useRef(null);
    const toggleNavbar = () => setNavExpanded((prevExpanded) => !prevExpanded);
    const checkCredentials = async (password) => {
        console.log("Checking credentials...", password);
        console.log("Stream ID:", streamId);
        const response = await authenticateUser(
            streamId,
            password,
            setPasswordProtected,
            setPasswordProtectedError,
            setPasswordVerified
        );
        return response; // Ensure the response is returned for validation
    };

    useEffect(() => {
        if (streamId && slug) {
            runContextSetters(
                streamId,
                slug,
                setActiveStatus,
                setHasNavigation,
                setBackgroundImage,
                setPasswordProtected,
                setStreamId,
                passwordVerified
            );

            const interval = setInterval(() => {
                runContextSetters(
                    streamId,
                    slug,
                    setActiveStatus,
                    setHasNavigation,
                    setBackgroundImage,
                    setPasswordProtected,
                    setStreamId,
                    passwordVerified
                );
            }, 15000);

            return () => clearInterval(interval);
        }
    }, [streamId, slug, passwordVerified]);

    return (
        <PlayerContext.Provider
            value={{
                slug,
                streamId,
                setStreamId,
                setBackgroundImage,
                activeTab,
                activeStatus,
                passwordProtected,
                checkCredentials,
                passwordProtectedError,
                setPasswordProtectedError,
                setPasswordVerified,
                hasNavigation,
                backgroundImage,
                navVisible,
                navExpanded,
                setNavExpanded,
                setActiveTab,
                toggleNavbar,
                setSlug,
                setNavVisible,
                hoverTimeout,
            }}
        >
            {children}
        </PlayerContext.Provider>
    );
};
